import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from 'react';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';

export interface AuthUser {
  id: number;
  email: string;
  name?: string | null;
  phone?: string | null;
  address_line1?: string | null;
  address_line2?: string | null;
  city?: string | null;
  postal_code?: string | null;
  country?: string | null;
  gender?: string | null;
  birthdate?: string | null;
  payment_label?: string | null;
  payment_details?: any;
}

interface AuthContextValue {
  user: AuthUser | null;
  token: string | null;
  loading: boolean;
  error: string | null;
  login: (email: string, password: string) => Promise<boolean>;
  register: (email: string, password: string, name?: string) => Promise<boolean>;
  verifyEmail: (email: string, code: string) => Promise<boolean>;
  logout: () => void;
  updateProfile: (data: Partial<AuthUser>) => Promise<boolean>;
  refreshProfile: () => Promise<void>;
  hasNewOrderNotification: boolean;
  setHasNewOrderNotification: React.Dispatch<React.SetStateAction<boolean>>;
  requestPasswordReset: (email: string) => Promise<boolean>;
  verifyResetCode: (email: string, code: string) => Promise<boolean>;
  resetPassword: (email: string, code: string, newPassword: string) => Promise<boolean>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

const TOKEN_KEY = 'auth_token';
const USER_KEY = 'auth_user';
const ORDER_BADGE_KEY = 'profile_order_notification';

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasNewOrderNotification, setHasNewOrderNotification] = useState(false);

  useEffect(() => {
    try {
      const storedToken = localStorage.getItem(TOKEN_KEY);
      const storedUser = localStorage.getItem(USER_KEY);
      if (storedToken) {
        setToken(storedToken);
      }
      if (storedUser) {
        const parsed = JSON.parse(storedUser) as AuthUser;
        setUser(parsed);
      }
    } catch (e) {
      console.error('Erreur lors du chargement des infos de session', e);
    }
  }, []);

  useEffect(() => {
    try {
      if (user) {
        const storedFlag = sessionStorage.getItem(ORDER_BADGE_KEY);
        setHasNewOrderNotification(storedFlag === '1');
      } else {
        setHasNewOrderNotification(false);
        sessionStorage.removeItem(ORDER_BADGE_KEY);
      }
    } catch (e) {
      console.error('Erreur lors du chargement de la notification de commande', e);
      setHasNewOrderNotification(false);
    }
  }, [user]);

  useEffect(() => {
    try {
      if (token) {
        localStorage.setItem(TOKEN_KEY, token);
      } else {
        localStorage.removeItem(TOKEN_KEY);
      }
    } catch (e) {
      console.error('Erreur lors de la sauvegarde du token', e);
    }
  }, [token]);

  useEffect(() => {
    try {
      if (user) {
        localStorage.setItem(USER_KEY, JSON.stringify(user));
      } else {
        localStorage.removeItem(USER_KEY);
      }
    } catch (e) {
      console.error('Erreur lors de la sauvegarde des infos utilisateur', e);
    }
  }, [user]);

  useEffect(() => {
    try {
      if (user && hasNewOrderNotification) {
        sessionStorage.setItem(ORDER_BADGE_KEY, '1');
      } else {
        sessionStorage.removeItem(ORDER_BADGE_KEY);
      }
    } catch (e) {
      console.error('Erreur lors de la sauvegarde de la notification de commande', e);
    }
  }, [user, hasNewOrderNotification]);

  const logout = () => {
    setUser(null);
    setToken(null);
    setError(null);
    setHasNewOrderNotification(false);
    try {
      sessionStorage.removeItem(ORDER_BADGE_KEY);
    } catch (e) {
      console.error('Erreur lors de la suppression de la notification de commande', e);
    }
  };

  const login = async (email: string, password: string): Promise<boolean> => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE_URL}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        const message = (data && data.error) || 'Erreur lors de la connexion.';
        setError(message);
        return false;
      }

      const data = await res.json();
      setToken(data.token);
      setUser(data.user as AuthUser);
      return true;
    } catch (e) {
      console.error('Erreur lors de la connexion', e);
      setError('Erreur réseau lors de la connexion.');
      return false;
    } finally {
      setLoading(false);
    }
  };

  const requestPasswordReset = async (email: string): Promise<boolean> => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE_URL}/auth/forgot-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        const message = (data && (data as any).error) || 'Erreur lors de la demande de réinitialisation.';
        setError(message);
        return false;
      }

      return true;
    } catch (e) {
      console.error('Erreur lors de la demande de réinitialisation de mot de passe', e);
      setError('Erreur réseau lors de la demande de réinitialisation de mot de passe.');
      return false;
    } finally {
      setLoading(false);
    }
  };

  const verifyResetCode = async (email: string, code: string): Promise<boolean> => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE_URL}/auth/verify-reset-code`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, code }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        const message = (data && (data as any).error) || 'Code de réinitialisation invalide ou expiré.';
        setError(message);
        return false;
      }

      return true;
    } catch (e) {
      console.error('Erreur lors de la vérification du code de réinitialisation', e);
      setError('Erreur réseau lors de la vérification du code de réinitialisation.');
      return false;
    } finally {
      setLoading(false);
    }
  };

  const resetPassword = async (
    email: string,
    code: string,
    newPassword: string
  ): Promise<boolean> => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE_URL}/auth/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, code, newPassword }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        const message = (data && (data as any).error) || 'Erreur lors de la réinitialisation du mot de passe.';
        setError(message);
        return false;
      }

      return true;
    } catch (e) {
      console.error('Erreur lors de la réinitialisation du mot de passe', e);
      setError('Erreur réseau lors de la réinitialisation du mot de passe.');
      return false;
    } finally {
      setLoading(false);
    }
  };

  const register = async (email: string, password: string, name?: string): Promise<boolean> => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE_URL}/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password, name }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        const message = (data && data.error) || "Erreur lors de la création du compte.";
        setError(message);
        return false;
      }

      const data = await res.json();
      if (!data || !data.success) {
        setError("Erreur lors de la création du compte.");
        return false;
      }
      return true;
    } catch (e) {
      console.error("Erreur lors de l'inscription", e);
      setError("Erreur réseau lors de l'inscription.");
      return false;
    } finally {
      setLoading(false);
    }
  };

  const verifyEmail = async (email: string, code: string): Promise<boolean> => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE_URL}/auth/verify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, code }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        const message = (data && data.error) || 'Erreur lors de la vérification du compte.';
        setError(message);
        return false;
      }

      const data = await res.json();
      setToken(data.token);
      setUser(data.user as AuthUser);
      return true;
    } catch (e) {
      console.error('Erreur lors de la vérification du compte', e);
      setError('Erreur réseau lors de la vérification du compte.');
      return false;
    } finally {
      setLoading(false);
    }
  };

  const refreshProfile = async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API_BASE_URL}/me`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!res.ok) {
        if (res.status === 401) {
          logout();
        }
        return;
      }

      const data = (await res.json()) as AuthUser;
      setUser(data);
    } catch (e) {
      console.error('Erreur lors de la récupération du profil', e);
    }
  };

  const updateProfile = async (data: Partial<AuthUser>): Promise<boolean> => {
    if (!token) return false;

    setLoading(true);
    setError(null);
    try {
      const current = user;
      const body = {
        name: data.name ?? current?.name ?? null,
        phone: data.phone ?? current?.phone ?? null,
        address_line1: data.address_line1 ?? current?.address_line1 ?? null,
        address_line2: data.address_line2 ?? current?.address_line2 ?? null,
        city: data.city ?? current?.city ?? null,
        postal_code: data.postal_code ?? current?.postal_code ?? null,
        country: data.country ?? current?.country ?? null,
        gender: data.gender ?? current?.gender ?? null,
        birthdate: data.birthdate ?? current?.birthdate ?? null,
        payment_label: data.payment_label ?? current?.payment_label ?? null,
        payment_details: data.payment_details ?? current?.payment_details ?? null,
      };

      const res = await fetch(`${API_BASE_URL}/me`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });

      if (!res.ok) {
        const resp = await res.json().catch(() => ({}));
        const message = (resp && resp.error) || 'Erreur lors de la mise à jour du profil.';
        setError(message);
        return false;
      }

      const updated = (await res.json()) as AuthUser;
      setUser(updated);
      return true;
    } catch (e) {
      console.error('Erreur lors de la mise à jour du profil', e);
      setError('Erreur réseau lors de la mise à jour du profil.');
      return false;
    } finally {
      setLoading(false);
    }
  };

  const value: AuthContextValue = {
    user,
    token,
    loading,
    error,
    login,
    register,
    verifyEmail,
    logout,
    updateProfile,
    refreshProfile,
    hasNewOrderNotification,
    setHasNewOrderNotification,
    requestPasswordReset,
    verifyResetCode,
    resetPassword,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth doit être utilisé dans un AuthProvider');
  }
  return ctx;
};
